import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteSwapPurposeResult,
  SwapPurpose,
  SwapPurposeApiItem,
  UpsertSwapPurposeRequest,
} from './swap-purpose.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapPurposeService {
  private readonly endpoint = apiUrl('swappurpose');

  constructor(private readonly http: HttpClient) {}

  getSwapPurposes(): Observable<SwapPurpose[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeSwapPurpose(item))),
    );
  }

  createSwapPurpose(payload: UpsertSwapPurposeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateSwapPurpose(id: number, payload: UpsertSwapPurposeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteSwapPurpose(id: number): Observable<DeleteSwapPurposeResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteSwapPurposeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteSwapPurposeResult>({
        kind: 'error',
        message: 'Unable to delete swap purpose at this time. Please try again.',
      })),
    );
  }

  private normalizeSwapPurpose(item: SwapPurposeApiItem): SwapPurpose {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: SwapPurposeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): SwapPurposeApiItem[] {
    if (Array.isArray(response)) {
      return response as SwapPurposeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as SwapPurposeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as SwapPurposeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
